import React, { useRef, useState, useEffect, useCallback } from 'react';

const HANDLE_SIZE = 8; // px, screen-space
const MIN_RECT_SIZE = 5; // PDF points

// Handle positions: [name, xFraction, yFraction]
const HANDLE_DEFS = [
  ['nw', 0, 0],
  ['n', 0.5, 0],
  ['ne', 1, 0],
  ['e', 1, 0.5],
  ['se', 1, 1],
  ['s', 0.5, 1],
  ['sw', 0, 1],
  ['w', 0, 0.5],
];

const HANDLE_CURSORS = {
  nw: 'nwse-resize',
  se: 'nwse-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
};

const RedactionPageCanvas = ({
  jobId,
  pageNumber,
  zoom,
  marks,
  activeTool,
  selectedMarkId,
  onDrawComplete,
  onSelectMark,
  onMoveMark,
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [drawCurrent, setDrawCurrent] = useState(null);
  const [naturalWidth, setNaturalWidth] = useState(0);
  const [naturalHeight, setNaturalHeight] = useState(0);

  // Interaction state kept in refs to avoid re-render churn during drag
  const interactionRef = useRef('idle'); // 'idle' | 'moving' | 'resizing'
  const dragDataRef = useRef(null);
  const previewOverridesRef = useRef(null); // { markId, x1, y1, x2, y2 }
  const rafRef = useRef(null);
  const [cursorStyle, setCursorStyle] = useState('default');

  const RENDER_DPI = 150;
  const PDF_DPI = 72;
  const dpiScale = RENDER_DPI / PDF_DPI;

  const pageWidth = naturalWidth / dpiScale;
  const pageHeight = naturalHeight / dpiScale;

  const imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/redaction/jobs/${jobId}/pages/${pageNumber}?dpi=${RENDER_DPI}`;

  const handleImageLoad = useCallback((e) => {
    setNaturalWidth(e.target.naturalWidth);
    setNaturalHeight(e.target.naturalHeight);
    setImageLoaded(true);
    setImageError(false);
  }, []);

  const getCanvasCoords = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  const canvasToPdfCoords = useCallback((canvasX, canvasY) => {
    return {
      x: canvasX / (dpiScale * zoom),
      y: canvasY / (dpiScale * zoom),
    };
  }, [dpiScale, zoom]);

  const pdfToCanvas = useCallback((pdfX, pdfY) => {
    return {
      x: pdfX * dpiScale * zoom,
      y: pdfY * dpiScale * zoom,
    };
  }, [dpiScale, zoom]);

  // Get effective mark coords (with preview override during drag)
  const getMarkCoords = useCallback((mark) => {
    const ov = previewOverridesRef.current;
    if (ov && ov.markId === mark.id) {
      return { x1: ov.x1, y1: ov.y1, x2: ov.x2, y2: ov.y2 };
    }
    return { x1: mark.x1, y1: mark.y1, x2: mark.x2, y2: mark.y2 };
  }, []);

  // Hit-test: returns { type: 'handle', handle, mark } | { type: 'body', mark } | null
  const getHitTarget = useCallback((canvasX, canvasY) => {
    const pageMarks = marks.filter((m) => m.page_number === pageNumber);

    // Check resize handles of selected mark first
    const selectedMark = pageMarks.find((m) => m.id === selectedMarkId);
    if (selectedMark) {
      const coords = getMarkCoords(selectedMark);
      const tl = pdfToCanvas(coords.x1, coords.y1);
      const br = pdfToCanvas(coords.x2, coords.y2);
      const w = br.x - tl.x;
      const h = br.y - tl.y;
      const half = HANDLE_SIZE / 2;

      for (const [name, fx, fy] of HANDLE_DEFS) {
        const hx = tl.x + w * fx;
        const hy = tl.y + h * fy;
        if (
          canvasX >= hx - half &&
          canvasX <= hx + half &&
          canvasY >= hy - half &&
          canvasY <= hy + half
        ) {
          return { type: 'handle', handle: name, mark: selectedMark };
        }
      }
    }

    // Check body of all marks (reverse to prefer topmost / last-drawn)
    for (let i = pageMarks.length - 1; i >= 0; i--) {
      const m = pageMarks[i];
      const coords = getMarkCoords(m);
      const pdf = canvasToPdfCoords(canvasX, canvasY);
      if (pdf.x >= coords.x1 && pdf.x <= coords.x2 && pdf.y >= coords.y1 && pdf.y <= coords.y2) {
        return { type: 'body', mark: m };
      }
    }

    return null;
  }, [marks, pageNumber, selectedMarkId, getMarkCoords, pdfToCanvas, canvasToPdfCoords]);

  // Redraw canvas
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageLoaded) return;

    const displayWidth = naturalWidth * zoom;
    const displayHeight = naturalHeight * zoom;

    canvas.width = displayWidth;
    canvas.height = displayHeight;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, displayWidth, displayHeight);

    const pageMarks = marks.filter((m) => m.page_number === pageNumber);
    pageMarks.forEach((mark) => {
      const coords = getMarkCoords(mark);
      const x = coords.x1 * dpiScale * zoom;
      const y = coords.y1 * dpiScale * zoom;
      const w = (coords.x2 - coords.x1) * dpiScale * zoom;
      const h = (coords.y2 - coords.y1) * dpiScale * zoom;

      const isSelected = mark.id === selectedMarkId;
      ctx.fillStyle = mark.is_confirmed
        ? 'rgba(239, 68, 68, 0.3)'
        : 'rgba(251, 191, 36, 0.3)';
      ctx.fillRect(x, y, w, h);

      ctx.strokeStyle = isSelected ? '#b91c1c' : mark.is_confirmed ? '#ef4444' : '#f59e0b';
      ctx.lineWidth = isSelected ? 2 : 1;
      ctx.strokeRect(x, y, w, h);

      // Draw resize handles on selected mark
      if (isSelected && activeTool !== 'draw') {
        const half = HANDLE_SIZE / 2;
        for (const [, fx, fy] of HANDLE_DEFS) {
          const hx = x + w * fx;
          const hy = y + h * fy;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(hx - half, hy - half, HANDLE_SIZE, HANDLE_SIZE);
          ctx.strokeStyle = '#2563eb';
          ctx.lineWidth = 1.5;
          ctx.strokeRect(hx - half, hy - half, HANDLE_SIZE, HANDLE_SIZE);
        }
      }
    });

    // Draw current drawing rectangle
    if (drawing && drawStart && drawCurrent) {
      const x = Math.min(drawStart.x, drawCurrent.x);
      const y = Math.min(drawStart.y, drawCurrent.y);
      const w = Math.abs(drawCurrent.x - drawStart.x);
      const h = Math.abs(drawCurrent.y - drawStart.y);

      ctx.fillStyle = 'rgba(239, 68, 68, 0.2)';
      ctx.fillRect(x, y, w, h);
      ctx.strokeStyle = '#ef4444';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash([]);
    }
  }, [marks, pageNumber, drawing, drawStart, drawCurrent, zoom, naturalWidth, naturalHeight, imageLoaded, selectedMarkId, dpiScale, activeTool, getMarkCoords]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  // Clamp coords to page bounds, enforce minimum size
  const clampRect = useCallback((x1, y1, x2, y2) => {
    return {
      x1: Math.max(0, Math.min(x1, pageWidth - MIN_RECT_SIZE)),
      y1: Math.max(0, Math.min(y1, pageHeight - MIN_RECT_SIZE)),
      x2: Math.max(MIN_RECT_SIZE, Math.min(x2, pageWidth)),
      y2: Math.max(MIN_RECT_SIZE, Math.min(y2, pageHeight)),
    };
  }, [pageWidth, pageHeight]);

  const handleMouseDown = useCallback((e) => {
    const coords = getCanvasCoords(e);

    if (activeTool === 'draw') {
      setDrawing(true);
      setDrawStart(coords);
      setDrawCurrent(coords);
      return;
    }

    const hit = getHitTarget(coords.x, coords.y);

    if (hit && hit.type === 'handle') {
      // Start resizing
      interactionRef.current = 'resizing';
      const markCoords = getMarkCoords(hit.mark);
      dragDataRef.current = {
        markId: hit.mark.id,
        handle: hit.handle,
        startCanvasX: coords.x,
        startCanvasY: coords.y,
        origX1: markCoords.x1,
        origY1: markCoords.y1,
        origX2: markCoords.x2,
        origY2: markCoords.y2,
      };
      return;
    }

    if (hit && hit.type === 'body') {
      onSelectMark?.(hit.mark);
      // Start moving
      interactionRef.current = 'moving';
      const markCoords = getMarkCoords(hit.mark);
      const pdf = canvasToPdfCoords(coords.x, coords.y);
      dragDataRef.current = {
        markId: hit.mark.id,
        offsetX: pdf.x - markCoords.x1,
        offsetY: pdf.y - markCoords.y1,
        origX1: markCoords.x1,
        origY1: markCoords.y1,
        origX2: markCoords.x2,
        origY2: markCoords.y2,
      };
      return;
    }

    // Clicked empty space — deselect
    onSelectMark?.(null);
  }, [activeTool, getCanvasCoords, getHitTarget, getMarkCoords, canvasToPdfCoords, onSelectMark]);

  const handleMouseMove = useCallback((e) => {
    const coords = getCanvasCoords(e);

    // Drawing mode
    if (drawing) {
      setDrawCurrent(coords);
      return;
    }

    const interaction = interactionRef.current;

    if (interaction === 'moving') {
      const dd = dragDataRef.current;
      const pdf = canvasToPdfCoords(coords.x, coords.y);
      const w = dd.origX2 - dd.origX1;
      const h = dd.origY2 - dd.origY1;

      let newX1 = pdf.x - dd.offsetX;
      let newY1 = pdf.y - dd.offsetY;

      // Clamp to page bounds
      newX1 = Math.max(0, Math.min(newX1, pageWidth - w));
      newY1 = Math.max(0, Math.min(newY1, pageHeight - h));

      previewOverridesRef.current = {
        markId: dd.markId,
        x1: newX1,
        y1: newY1,
        x2: newX1 + w,
        y2: newY1 + h,
      };

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(redraw);
      return;
    }

    if (interaction === 'resizing') {
      const dd = dragDataRef.current;
      const pdf = canvasToPdfCoords(coords.x, coords.y);
      let { origX1: nx1, origY1: ny1, origX2: nx2, origY2: ny2 } = dd;

      // Adjust edges based on which handle is dragged
      const handle = dd.handle;
      if (handle.includes('w')) nx1 = Math.min(pdf.x, nx2 - MIN_RECT_SIZE);
      if (handle.includes('e')) nx2 = Math.max(pdf.x, nx1 + MIN_RECT_SIZE);
      if (handle.includes('n')) ny1 = Math.min(pdf.y, ny2 - MIN_RECT_SIZE);
      if (handle.includes('s')) ny2 = Math.max(pdf.y, ny1 + MIN_RECT_SIZE);

      const clamped = clampRect(nx1, ny1, nx2, ny2);
      // Re-enforce minimum after clamp
      if (clamped.x2 - clamped.x1 < MIN_RECT_SIZE) clamped.x2 = clamped.x1 + MIN_RECT_SIZE;
      if (clamped.y2 - clamped.y1 < MIN_RECT_SIZE) clamped.y2 = clamped.y1 + MIN_RECT_SIZE;

      previewOverridesRef.current = { markId: dd.markId, ...clamped };

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(redraw);
      return;
    }

    // Hover cursor when idle and not drawing
    if (activeTool !== 'draw') {
      const hit = getHitTarget(coords.x, coords.y);
      if (hit && hit.type === 'handle') {
        setCursorStyle(HANDLE_CURSORS[hit.handle]);
      } else if (hit && hit.type === 'body') {
        setCursorStyle('move');
      } else {
        setCursorStyle('default');
      }
    }
  }, [drawing, getCanvasCoords, canvasToPdfCoords, pageWidth, pageHeight, redraw, clampRect, activeTool, getHitTarget]);

  const handleMouseUp = useCallback(() => {
    // Handle draw mode
    if (drawing) {
      if (drawStart && drawCurrent) {
        const x1 = Math.min(drawStart.x, drawCurrent.x);
        const y1 = Math.min(drawStart.y, drawCurrent.y);
        const x2 = Math.max(drawStart.x, drawCurrent.x);
        const y2 = Math.max(drawStart.y, drawCurrent.y);

        if (x2 - x1 > 5 && y2 - y1 > 5) {
          const pdfTopLeft = canvasToPdfCoords(x1, y1);
          const pdfBottomRight = canvasToPdfCoords(x2, y2);

          onDrawComplete({
            page_number: pageNumber,
            x1: pdfTopLeft.x,
            y1: pdfTopLeft.y,
            x2: pdfBottomRight.x,
            y2: pdfBottomRight.y,
          });
        }
      }

      setDrawing(false);
      setDrawStart(null);
      setDrawCurrent(null);
      return;
    }

    // Handle move/resize completion
    const interaction = interactionRef.current;
    const preview = previewOverridesRef.current;

    if ((interaction === 'moving' || interaction === 'resizing') && preview) {
      const dd = dragDataRef.current;
      // Only save if coordinates actually changed
      const moved =
        Math.abs(preview.x1 - dd.origX1) > 0.01 ||
        Math.abs(preview.y1 - dd.origY1) > 0.01 ||
        Math.abs(preview.x2 - dd.origX2) > 0.01 ||
        Math.abs(preview.y2 - dd.origY2) > 0.01;

      if (moved && onMoveMark) {
        onMoveMark(preview.markId, {
          x1: preview.x1,
          y1: preview.y1,
          x2: preview.x2,
          y2: preview.y2,
        });
      }
    }

    interactionRef.current = 'idle';
    dragDataRef.current = null;
    previewOverridesRef.current = null;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    redraw();
  }, [drawing, drawStart, drawCurrent, canvasToPdfCoords, onDrawComplete, pageNumber, onMoveMark, redraw]);

  const displayWidth = naturalWidth * zoom;
  const displayHeight = naturalHeight * zoom;

  const canvasCursor = activeTool === 'draw' ? 'crosshair' : cursorStyle;

  return (
    <div ref={containerRef} className="relative inline-block">
      <img
        ref={imgRef}
        src={imageUrl}
        alt={`Page ${pageNumber}`}
        onLoad={handleImageLoad}
        onError={() => setImageError(true)}
        style={{
          width: imageLoaded ? `${displayWidth}px` : 'auto',
          height: imageLoaded ? `${displayHeight}px` : 'auto',
          maxWidth: 'none',
        }}
        className="block"
        crossOrigin="use-credentials"
      />
      {imageError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <p className="text-sm text-red-600">Failed to load page image</p>
        </div>
      )}
      {imageLoaded && (
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: `${displayWidth}px`,
            height: `${displayHeight}px`,
            cursor: canvasCursor,
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => {
            if (drawing || interactionRef.current !== 'idle') handleMouseUp();
          }}
        />
      )}
    </div>
  );
};

export default RedactionPageCanvas;
